<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import GgSupportSelect from './GgSupportSelect.vue';
import GgSupportSearch from './GgSupportSearch.vue';
import {
  emptyForm,
  referenceModels,
  referenceCatalogs,
  rulesFor,
} from './form';

const props = defineProps({
  modelValue: { type: Object, required: true },
  schema: { type: Object, required: true },
  conversationId: { type: Number, required: true },
  customerSearchAvailable: { type: Boolean, default: false },
  scheduleUsers: { type: Array, default: () => [] },
  errors: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const form = computed(() => props.modelValue);
const rules = computed(() => rulesFor(props.schema, form.value));
const eligibleItems = computed(() =>
  (form.value.order?.items || []).filter(item => item.requires_activation)
);
const update = (key, value) =>
  emit('update:modelValue', { ...form.value, [key]: value });
const optionsFor = values => values.map(value => ({ value, label: value }));
const resetReferences = () => ({
  ...emptyForm(form.value),
  description: form.value.description,
  due_at: form.value.due_at,
});
const updateType = value => {
  const type = props.schema.types.find(item => String(item.id) === value);
  emit('update:modelValue', {
    ...resetReferences(),
    type_id: type.id,
    type: type.value,
  });
};
const updateRental = value =>
  emit('update:modelValue', {
    ...resetReferences(),
    rental_demonstration: value,
  });
const updateRepresentative = value =>
  emit('update:modelValue', {
    ...form.value,
    customer_is_representative: value,
    representative: null,
  });
const updateReference = (key, value) => {
  if (key === 'equipment_ids') {
    if (value && !form.value.equipment.some(item => item.id === value.id))
      update('equipment', [...form.value.equipment, value]);
    return;
  }
  const next = { ...form.value, [referenceModels[key]]: value };
  if (key === 'order') next.order_item_ids = [];
  emit('update:modelValue', next);
};
const toggleItem = (id, checked) =>
  update(
    'order_item_ids',
    checked
      ? [...form.value.order_item_ids, id]
      : form.value.order_item_ids.filter(value => value !== id)
  );
const toggleParticipant = (id, checked) =>
  update(
    'participant_user_ids',
    checked
      ? [...form.value.participant_user_ids, id]
      : form.value.participant_user_ids.filter(value => value !== id)
  );
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="m-0 text-xs text-n-slate-11">{{ t('GG_SUPPORT.NUMBER_HINT') }}</p>
    <GgSupportSelect
      id="gg-support-type"
      :model-value="form.type_id === null ? '' : String(form.type_id)"
      :label="t('GG_SUPPORT.TYPE_SELECT')"
      :options="
        schema.types.map(type => ({
          value: String(type.id),
          label: type.value,
        }))
      "
      :disabled="disabled"
      :error="errors.type_id ? t(errors.type_id) : ''"
      @update:model-value="updateType"
    />
    <label
      v-if="rules.allowsRental"
      class="flex items-center gap-2 text-sm text-n-slate-12"
    >
      <input
        type="checkbox"
        :checked="form.rental_demonstration"
        :disabled="disabled"
        @change="updateRental($event.target.checked)"
      />
      {{ t('GG_SUPPORT.FIELDS.rental_demonstration') }}
    </label>
    <div v-for="key in rules.fields" :key="key" class="flex flex-col gap-2">
      <GgSupportSearch
        v-if="key === 'customer_id'"
        :model-value="form.customer"
        :conversation-id="conversationId"
        :available="customerSearchAvailable"
        :disabled="disabled"
        :label="
          t('GG_SUPPORT.FIELDS.customer_reference') +
          (rules.required.includes(key) ? ' *' : '')
        "
        :error="errors[key] ? t(errors[key]) : ''"
        @update:model-value="updateReference(key, $event)"
      />
      <fieldset
        v-else-if="key === 'order_item_ids'"
        class="m-0 rounded-lg border border-n-weak p-3 flex flex-col gap-2"
      >
        <legend class="px-1 text-sm font-medium text-n-slate-12">
          {{ t('GG_SUPPORT.FIELDS.order_item_ids') }} *
        </legend>
        <p v-if="!eligibleItems.length" class="m-0 text-xs text-n-slate-11">
          {{ t('GG_SUPPORT.CATALOG_SEARCH.SELECT_ORDER') }}
        </p>
        <label
          v-for="item in eligibleItems"
          :key="item.id"
          class="flex items-start gap-2 text-sm text-n-slate-12"
        >
          <input
            :id="`gg-support-order-item-${item.id}`"
            type="checkbox"
            :checked="form.order_item_ids.includes(item.id)"
            :aria-invalid="!!errors.order_item_ids"
            :disabled="
              disabled ||
              (!form.order_item_ids.includes(item.id) &&
                form.order_item_ids.length >= rules.max_order_item_ids)
            "
            @change="toggleItem(item.id, $event.target.checked)"
          />
          <span
            >{{ item.id }} · {{ item.name
            }}<span v-if="item.serial"> · {{ item.serial }}</span>
            <span
              v-if="!item.equipment_id"
              class="block text-xs text-n-slate-11"
              >{{ t('GG_SUPPORT.CATALOG_SEARCH.NO_EQUIPMENT') }}</span
            ></span
          >
        </label>
        <p v-if="errors[key]" role="alert" class="m-0 text-xs text-n-ruby-11">
          {{ t(errors[key]) }}
        </p>
      </fieldset>
      <GgSupportSearch
        v-else
        :model-value="
          key === 'equipment_ids' ? null : form[referenceModels[key]]
        "
        :conversation-id="conversationId"
        :type-id="form.type_id"
        :catalog="referenceCatalogs[key]"
        :multiple="key === 'equipment_ids'"
        :selected-ids="
          key === 'equipment_ids' ? form.equipment.map(item => item.id) : []
        "
        :order-kinds="key === 'order' ? rules.order_kinds : []"
        available
        :disabled="
          disabled ||
          (key === 'equipment_ids' &&
            form.equipment.length >= rules.max_equipment_ids)
        "
        :label="
          t(`GG_SUPPORT.FIELDS.${key}`) +
          (rules.required.includes(key) ? ' *' : '')
        "
        :error="errors[key] ? t(errors[key]) : ''"
        @update:model-value="updateReference(key, $event)"
      />
      <ul
        v-if="key === 'equipment_ids' && form.equipment.length"
        class="m-0 p-0 list-none flex flex-col gap-1"
      >
        <li
          v-for="item in form.equipment"
          :key="item.id"
          class="flex items-center justify-between gap-2 rounded-lg bg-n-alpha-2 p-2 text-xs text-n-slate-12"
        >
          <span class="break-words"
            >{{ item.id }} · {{ item.name }} · {{ item.serial }}</span
          >
          <Button
            icon="i-lucide-x"
            size="sm"
            variant="ghost"
            color="slate"
            :aria-label="
              t('GG_SUPPORT.CATALOG_SEARCH.REMOVE', { name: item.name })
            "
            :disabled="disabled"
            @click="
              update(
                'equipment',
                form.equipment.filter(value => value.id !== item.id)
              )
            "
          />
        </li>
      </ul>
      <p
        v-if="key === 'order' && form.order"
        class="m-0 text-xs text-n-slate-11"
      >
        {{
          t('GG_SUPPORT.CATALOG_SEARCH.ORDER_SUMMARY', {
            customer: form.order.customer_id,
            count: form.order.items.length,
          })
        }}
      </p>
      <label
        v-if="key === 'customer_id' && rules.allowsRepresentative"
        class="flex items-center gap-2 text-xs text-n-slate-12"
      >
        <input
          type="checkbox"
          :checked="form.customer_is_representative"
          :disabled="disabled || !form.customer"
          @change="updateRepresentative($event.target.checked)"
        />
        {{ t('GG_SUPPORT.FIELDS.customer_is_representative') }}
      </label>
    </div>
    <p
      v-if="
        rules.automatic_activation_per_order &&
        form.order?.kind === 'Venda' &&
        eligibleItems.length
      "
      role="note"
      class="m-0 rounded-lg bg-n-blue-2 p-3 text-xs text-n-blue-11"
    >
      {{ t('GG_SUPPORT.CATALOG_SEARCH.AUTO_ACTIVATION') }}
    </p>
    <GgSupportSelect
      id="gg-support-priority"
      :model-value="form.priority"
      :label="t('GG_SUPPORT.PRIORITY_SELECT')"
      :options="optionsFor(schema.priorities)"
      :disabled="disabled"
      @update:model-value="update('priority', $event)"
    />
    <GgSupportSelect
      id="gg-support-origin"
      :model-value="form.origin"
      :label="t('GG_SUPPORT.ORIGIN_SELECT')"
      :options="optionsFor(schema.origins)"
      :disabled="disabled"
      @update:model-value="update('origin', $event)"
    />
    <Input
      id="gg-support-due-at"
      :model-value="form.due_at"
      :label="t('GG_SUPPORT.FIELDS.due_at')"
      type="datetime-local"
      :disabled="disabled"
      @update:model-value="update('due_at', $event)"
    />
    <fieldset
      v-if="rules.scheduled"
      class="m-0 p-3 border border-n-weak rounded-lg flex flex-col gap-3"
    >
      <legend class="px-1 text-sm font-medium text-n-slate-12">
        {{ t('GG_SUPPORT.SCHEDULE') }}
      </legend>
      <Input
        v-for="key in ['starts_at', 'ends_at']"
        :id="`gg-support-${key}`"
        :key="key"
        :model-value="form[key]"
        :label="t(`GG_SUPPORT.FIELDS.${key}`) + ' *'"
        type="datetime-local"
        :disabled="disabled"
        :message="errors[key] ? t(errors[key]) : ''"
        :message-type="errors[key] ? 'error' : 'info'"
        :aria-invalid="!!errors[key]"
        @update:model-value="update(key, $event)"
      />
      <Input
        :model-value="form.location"
        :label="t('GG_SUPPORT.FIELDS.location')"
        :disabled="disabled"
        :maxlength="200"
        @update:model-value="update('location', $event)"
      />
      <span class="text-sm font-medium text-n-slate-12">{{
        t('GG_SUPPORT.FIELDS.participant_user_ids')
      }}</span>
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('GG_SUPPORT.CATALOG_SEARCH.PARTICIPANTS_HINT') }}
      </p>
      <div class="max-h-40 overflow-y-auto flex flex-col gap-2">
        <label
          v-for="person in scheduleUsers"
          :key="person.usuario_id"
          class="flex items-center gap-2 text-xs text-n-slate-12"
        >
          <input
            type="checkbox"
            :checked="form.participant_user_ids.includes(person.usuario_id)"
            :disabled="
              disabled ||
              (!form.participant_user_ids.includes(person.usuario_id) &&
                form.participant_user_ids.length >= 50)
            "
            @change="
              toggleParticipant(person.usuario_id, $event.target.checked)
            "
          />
          {{ person.name }}
        </label>
      </div>
    </fieldset>
    <p class="m-0 text-xs text-n-slate-11">{{ t('GG_SUPPORT.DATE_HINT') }}</p>
    <div class="flex flex-col gap-2">
      <label
        for="gg-support-description"
        class="text-sm font-medium text-n-slate-12"
        >{{ t('GG_SUPPORT.FIELDS.description') }} *</label
      >
      <textarea
        id="gg-support-description"
        :value="form.description"
        rows="3"
        maxlength="4000"
        :disabled="disabled"
        :aria-invalid="!!errors.description"
        :placeholder="t('GG_SUPPORT.DESCRIPTION_HINT')"
        class="w-full rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
        @input="update('description', $event.target.value)"
      />
      <p v-if="errors.description" class="m-0 text-xs text-n-ruby-11">
        {{ t(errors.description) }}
      </p>
    </div>
  </div>
</template>
